import React, { useState } from 'react';
import { Box, Typography, TextField, MenuItem, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Icon as OpusIcon, Toggle } from '../common/opus';
import {
    FALLBACK_MAESTRO_FIELDS,
    PREVIEW_SOURCE_OPTIONS,
    fieldsInGroup,
    getFieldFormValue,
} from '../../utils/maestroSettings';

const GROUP_SX = {
    background: 'var(--surface-2)',
    borderRadius: '10px',
    px: 2,
    py: 1.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
};

const GROUP_TITLE_SX = {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
};

// Two columns once there's room, one on narrow screens.
const TWO_COLUMNS_SX = {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
    gap: 1.5,
    alignItems: 'start',
};

const GROUPS = [
    { group: 'locked', title: 'Locked for operators', icon: 'lock', color: 'var(--coral)' },
    { group: 'initial', title: 'Starting view · operator can change', color: 'var(--green)' },
];

// Input for one configured field, by its fieldType.
const FieldControl = ({ field, value, error, disabled, onChange }) => {
    if (field.fieldType === 'boolean') {
        return <Toggle checked={Boolean(value)} label={field.label} disabled={disabled} onChange={onChange} />;
    }
    const isNumber = field.fieldType === 'number';
    return (
        <TextField
            size="small"
            type={isNumber ? 'number' : 'text'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            error={!!error}
            helperText={error}
            disabled={disabled}
            inputProps={{ 'aria-label': field.label }}
            sx={{ width: isNumber ? 120 : 200, flexShrink: 0 }}
        />
    );
};

const FieldGroup = ({ title, icon, color, fields, form, errors, disabled, onFieldChange }) => (
    <Box sx={GROUP_SX}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color, mb: 0.5 }}>
            {icon && <OpusIcon name={icon} size={13} />}
            <Typography sx={{ ...GROUP_TITLE_SX, color }}>{title}</Typography>
        </Box>
        {fields.map((field) => (
            <Box key={field.fieldName} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, minHeight: 40 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                        {field.label}{field.required && field.fieldType !== 'boolean' ? ' *' : ''}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                        {field.fieldName}
                    </Typography>
                </Box>
                <FieldControl
                    field={field}
                    value={getFieldFormValue(form, field)}
                    error={errors?.[field.fieldName]}
                    disabled={disabled}
                    onChange={(next) => onFieldChange(field, next)}
                />
            </Box>
        ))}
    </Box>
);

// The session's preview source. Endpoint, renderer destination and API key
// only apply to (and only show for) a Flow WHEP preview. The API key is typed
// in by hand for now, and hidden unless the eye is pressed.
const PreviewSourceFields = ({ value, onChange, errors = {}, disabled }) => {
    const [showApiKey, setShowApiKey] = useState(false);
    const set = (key, next) => onChange({ ...value, [key]: next });
    const whep = value.source === 'whep';
    return (
        <Box sx={GROUP_SX}>
            <Typography sx={{ ...GROUP_TITLE_SX, mb: 1 }}>Preview source</Typography>
            <Box sx={TWO_COLUMNS_SX}>
                <TextField
                    select
                    size="small"
                    label="Source"
                    value={value.source}
                    onChange={(e) => set('source', e.target.value)}
                    helperText={value.source ? undefined : 'Operators keep their own preview settings'}
                    disabled={disabled}
                    fullWidth
                >
                    {PREVIEW_SOURCE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                </TextField>
                {whep && (
                    <>
                        <TextField
                            size="small"
                            label="WHEP endpoint"
                            placeholder="https://<flow-host>/whep/<stream>"
                            value={value.endpoint}
                            onChange={(e) => set('endpoint', e.target.value)}
                            error={!!errors.endpoint}
                            helperText={errors.endpoint}
                            disabled={disabled}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Renderer destination"
                            placeholder="…_PREVIEW"
                            value={value.rendererId}
                            onChange={(e) => set('rendererId', e.target.value)}
                            error={!!errors.rendererId}
                            helperText={errors.rendererId || 'Preview broker destination'}
                            disabled={disabled}
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Flow API key"
                            type={showApiKey ? 'text' : 'password'}
                            autoComplete="new-password"
                            value={value.apiKey}
                            onChange={(e) => set('apiKey', e.target.value)}
                            error={!!errors.apiKey}
                            helperText={errors.apiKey}
                            disabled={disabled}
                            fullWidth
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            edge="end"
                                            aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                                            onClick={() => setShowApiKey((shown) => !shown)}
                                        >
                                            {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </>
                )}
            </Box>
        </Box>
    );
};

// ─── Maestro Setup Fields ─────────────────────────────────────────────────────
// Session-level Maestro setup for AeDrive / Copilot sessions, shared by the
// create dialog, the edit dialog and the session details page. Controlled:
// `value` is the form from maestroFormFromSession(), `onChange` gets the next
// form. The header toggle (value.enabled) reveals the fields; off means no
// Maestro setup is stored and operators keep their own saved settings.
// `fields` is the configured field list (useSessionFields); each group renders
// only when it has fields.
const MaestroSetupFields = ({
    value, onChange, fields = FALLBACK_MAESTRO_FIELDS, errors = {}, hasSuggestedPreview = false, disabled = false,
}) => {
    const setField = (field, next) => onChange({ ...value, [field.group]: { ...value[field.group], [field.fieldName]: next } });
    const groups = GROUPS
        .map((group) => ({ ...group, fields: fieldsInGroup(fields, group.group) }))
        .filter((group) => group.fields.length > 0);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Maestro setup</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--text-dim)' }}>
                        {value.enabled
                            ? 'AeDrive and Copilot only'
                            : 'Off — operators keep their own saved settings'}
                    </Typography>
                </Box>
                <Toggle
                    checked={value.enabled}
                    label="Maestro setup"
                    disabled={disabled}
                    onChange={(next) => onChange({ ...value, enabled: next })}
                />
            </Box>
            {value.enabled && (
                <>
                    <Box sx={TWO_COLUMNS_SX}>
                        <TextField
                            size="small"
                            label="Preview renderer URL"
                            placeholder="https://…/?renderer=…_PREVIEW"
                            value={value.previewRendererUrl}
                            onChange={(e) => onChange({ ...value, previewRendererUrl: e.target.value })}
                            error={!!errors.previewRendererUrl}
                            helperText={errors.previewRendererUrl
                                || (hasSuggestedPreview ? 'Filled from the renderer’s preview renderer' : 'The selected renderer has no preview renderer')}
                            disabled={disabled}
                            fullWidth
                        />
                    </Box>
                    <PreviewSourceFields
                        value={value.flowPreview}
                        onChange={(flowPreview) => onChange({ ...value, flowPreview })}
                        errors={errors.flowPreview}
                        disabled={disabled}
                    />
                    {groups.length > 0 && (
                        <Box sx={TWO_COLUMNS_SX}>
                            {groups.map((group) => (
                                <FieldGroup
                                    key={group.group}
                                    title={group.title}
                                    icon={group.icon}
                                    color={group.color}
                                    fields={group.fields}
                                    form={value}
                                    errors={errors.fields}
                                    disabled={disabled}
                                    onFieldChange={setField}
                                />
                            ))}
                        </Box>
                    )}
                    <Typography sx={{ fontSize: 12, color: 'var(--text-dim)', lineHeight: 1.45 }}>
                        Locked settings show greyed in the operator’s cog, marked “set by the host”. Anything not listed
                        here uses the operator’s own saved setting. Starting values shown are aeDrive’s current defaults.
                    </Typography>
                </>
            )}
        </Box>
    );
};

export default MaestroSetupFields;
